// Must be FIRST import: registers embedded sandbox-worker re-exec support (see guard at EOF).
import '@gitlab-org/sandbox/worker';
// Must come before any import that (transitively) loads chalk/ink: installs the
// --no-color / NO_COLOR stdout/stderr filter and the FORCE_COLOR override,
// which chalk reads once at load time (see the module's doc comment).
import './utils/init_color_early';
import { Command } from 'commander';

import { SanitizedError } from '@gitlab-org/errors';
import { getLanguageServerVersion } from '@gitlab-org/core';
import { CommandBootstrapper } from './commands/command_bootstrapper';
import { RootCommand } from './commands/root_command';
import { ExitHandler } from './utils/exit';
import { UsageError } from './utils/usage_error';
import { buildFatalErrorMessage } from './cli_constants';
import { type CliGlobalExtensions } from './di';

// Create exit handler early - registers SIGINT/SIGTERM handlers immediately
const exitHandler = new ExitHandler();

const program = new Command();

const logAndExit = async (error: unknown) => {
  if (error instanceof UsageError) {
    // Bad invocation, not a bug: print only the message — no crash framing,
    // no error tracking. (In json mode the machine-readable error document
    // was already written to stdout; this goes to stderr.)
    await exitHandler.exit(1, { finalMessage: error.message });
    return;
  }

  const errorHandler = (global as CliGlobalExtensions).__errorHandler; // eslint-disable-line no-underscore-dangle

  if (errorHandler) {
    // DI has initialised - use ErrorHandler which logs and tracks the error.
    const sanitizedError = new SanitizedError('GitLab Duo CLI uncaught exception', error);
    errorHandler.handleError(sanitizedError.message, sanitizedError);
    // Flush Sentry (and any other resources) before exiting so in-flight
    // error reports are not dropped by the process terminating too early.
    await errorHandler.dispose?.();
  } else {
    // DI not yet initialised: no logger/tracker exists, so write the full
    // error (with stack) to stderr to keep a pre-DI crash debuggable.
    // eslint-disable-next-line no-console
    console.error('GitLab Duo CLI uncaught exception', error);
  }

  const errorMessage = error instanceof Error ? error.message : String(error);
  await exitHandler.exit(1, { finalMessage: buildFatalErrorMessage(errorMessage) });
};
process.on('unhandledRejection', logAndExit);
process.on('uncaughtException', logAndExit);

const root = new RootCommand({
  version: getLanguageServerVersion(),
  exitHandler,
  program,
});

root.register(program);
new CommandBootstrapper().attach(program, root.children ?? []);

if (process.env.GITLAB_SANDBOX_WORKER !== 'true') {
  try {
    await program.parseAsync(process.argv);
  } catch (error) {
    await logAndExit(error);
  }
}
